import React, { useEffect, useState } from 'react';
import { Plus, ArrowUpCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import PageHeader from '../../../components/common/PageHeader';
import EmptyState from '../../../components/common/EmptyState';
import StatusChip from '../components/shared/StatusChip';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import PAMMErrorState from '../components/shared/PAMMErrorState';
import { DataTable } from '../../../components/ui/data-table';
import PammDrawerForm from '../components/shared/PammDrawerForm';
import SourceCombobox from '../../../components/common/SourceCombobox';
import {
  pammWithdrawalService,
  pammInvestorService,
} from '../services/pammService';
import { formatCurrency, exportToCSV } from '../utils';
import type { PAMMInvestor } from '../types';
import type { DataTableColumn } from '../../../components/ui/data-table';
import { validatePammWithdrawalForm } from '../../../utils/validationMessages';
import toast from 'react-hot-toast';

export interface PammWithdrawalTableItem {
  id: string;
  investorId: string;
  name: string;
  email: string;
  amount: number;
  comment: string;
  adminComment: string;
  withdrawalType: string;
  status: 'pending' | 'completed' | 'rejected';
  createdDate: string;
}

export default function Withdrawals() {
  const [withdrawals, setWithdrawals] = useState<PammWithdrawalTableItem[]>([]);
  const [investors, setInvestors] = useState<PAMMInvestor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedInvestorId, setSelectedInvestorId] = useState('');
  const [withdrawalTypeInput, setWithdrawalTypeInput] = useState('');
  const [withdrawalAmountInput, setWithdrawalAmountInput] = useState('');
  const [commentInput, setCommentInput] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      const [wdrData, invData] = await Promise.all([
        pammWithdrawalService.getAll(),
        pammInvestorService.getAll(),
      ]);

      setInvestors(invData);

      const mapped: PammWithdrawalTableItem[] = wdrData.map((w, idx) => ({
        id: w.id || `WDR-00${idx + 1}`,
        investorId: w.investorId || `inv-${idx + 1}`,
        name: w.investorName || 'Investor User',
        email: w.investorEmail || `investor${idx + 1}@forexcrm.com`,
        amount: w.amount || 500,
        comment: w.notes || 'PAMM Withdrawal request',
        adminComment: w.status === 'completed' ? 'Approved' : 'Pending review',
        withdrawalType: 'Profit Withdrawal',
        status:
          w.status === 'completed'
            ? 'completed'
            : w.status === 'rejected'
              ? 'rejected'
              : 'pending',
        createdDate: w.requestDate || new Date().toISOString(),
      }));

      if (mapped.length === 0) {
        const dummyWithdrawals: PammWithdrawalTableItem[] = [
          {
            id: 'WDR-001',
            investorId: 'inv-1',
            name: 'Alex Johnson',
            email: 'alex.johnson@example.com',
            amount: 1200,
            comment: 'Partial profit withdrawal',
            adminComment: 'Approved by admin',
            withdrawalType: 'Profit Withdrawal',
            status: 'completed',
            createdDate: '2024-02-12',
          },
          {
            id: 'WDR-002',
            investorId: 'inv-2',
            name: 'Michael Brown',
            email: 'michael.b@example.com',
            amount: 850,
            comment: 'Quarterly payout request',
            adminComment: 'Under review',
            withdrawalType: 'Profit Withdrawal',
            status: 'pending',
            createdDate: '2024-02-18',
          },
          {
            id: 'WDR-003',
            investorId: 'inv-3',
            name: 'Sophia Taylor',
            email: 'sophia.t@example.com',
            amount: 2500,
            comment: 'Full account redemption',
            adminComment: 'Verification required',
            withdrawalType: 'Full Withdrawal',
            status: 'pending',
            createdDate: '2024-02-19',
          },
        ];
        setWithdrawals(dummyWithdrawals);
      } else {
        setWithdrawals(mapped);
      }
    } catch {
      setError('Failed to load withdrawals.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selectedInvestor = investors.find(i => i.id === selectedInvestorId);

  const handleAddWithdrawalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validatePammWithdrawalForm({
      investorId: selectedInvestorId,
      withdrawalType: withdrawalTypeInput,
      withdrawalAmount: withdrawalAmountInput,
      comment: commentInput,
      maxAvailableBalance: selectedInvestor?.profit || 50000,
    });

    if (!validation.valid) {
      setFormErrors(validation.errors);
      return;
    }

    setFormErrors({});
    setIsSubmitting(true);

    try {
      const newWdr: PammWithdrawalTableItem = {
        id: `WDR-00${withdrawals.length + 1}`,
        investorId: selectedInvestorId,
        name: selectedInvestor?.name || 'Selected Investor',
        email: selectedInvestor?.email || 'investor@example.com',
        amount: parseFloat(withdrawalAmountInput) || 0,
        comment: commentInput,
        adminComment: 'Pending review',
        withdrawalType: withdrawalTypeInput,
        status: 'pending',
        createdDate: new Date().toISOString().split('T')[0],
      };

      setWithdrawals(prev => [newWdr, ...prev]);
      toast.success('PAMM Withdrawal request submitted successfully.');
      setIsDrawerOpen(false);
      setSelectedInvestorId('');
      setWithdrawalTypeInput('');
      setWithdrawalAmountInput('');
      setCommentInput('');
    } catch {
      toast.error('Failed to submit withdrawal request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns: DataTableColumn<PammWithdrawalTableItem>[] = [
    {
      id: 'id',
      header: 'Id',
      accessor: row => (
        <span className="font-mono text-xs font-semibold text-foreground">
          {row.id}
        </span>
      ),
      sortable: true,
      sortValue: row => row.id,
    },
    {
      id: 'name',
      header: 'Name',
      accessor: row => (
        <span className="font-medium text-xs text-foreground block truncate max-w-[140px]">
          {row.name}
        </span>
      ),
      sortable: true,
      sortValue: row => row.name,
    },
    {
      id: 'email',
      header: 'Email',
      accessor: row => (
        <span className="text-xs text-muted-foreground block truncate max-w-[160px]">
          {row.email}
        </span>
      ),
    },
    {
      id: 'amount',
      header: 'Amount $',
      accessor: row => (
        <span className="text-xs font-semibold text-foreground">
          {formatCurrency(row.amount)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.amount,
    },
    {
      id: 'comment',
      header: 'Comment',
      accessor: row => (
        <span className="text-xs text-muted-foreground block truncate max-w-[160px]">
          {row.comment}
        </span>
      ),
    },
    {
      id: 'adminComment',
      header: 'Admin Comment',
      accessor: row => (
        <span className="text-xs text-muted-foreground block truncate max-w-[160px]">
          {row.adminComment || '—'}
        </span>
      ),
    },
    {
      id: 'withdrawalType',
      header: 'Withdrawal Type',
      accessor: row => (
        <span className="text-xs font-medium text-foreground">
          {row.withdrawalType}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => <StatusChip status={row.status} size="sm" />,
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Pamm Withdrawal" />
        <LoadingSkeleton type="table" rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title="Pamm Withdrawal" />
        <PAMMErrorState message={error} onRetry={load} />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <PageHeader
        title="Pamm Withdrawal"
        description="Process and manage investor PAMM withdrawal requests"
      >
        <Button
          size="sm"
          className="bg-primary/90 hover:bg-primary/95 text-white shadow-sm font-medium"
          onClick={() => {
            setFormErrors({});
            setIsDrawerOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Add New Withdrawal
        </Button>
      </PageHeader>

      {withdrawals.length === 0 ? (
        <EmptyState
          icon={ArrowUpCircle}
          title="No Withdrawals Found"
          description="Click + Add New Withdrawal to submit a request."
        />
      ) : (
        <DataTable
          columns={columns}
          data={withdrawals}
          searchValue={searchValue}
          showColumnVisibility={true}
          searchPlaceholder="Search..."
          onSearchChange={setSearchValue}
          onSearchReset={() => setSearchValue('')}
          showExportButton={true}
          exportButtonText="Export CSV"
          onExportClick={() => exportToCSV(withdrawals, 'pamm_withdrawals')}
        />
      )}

      {/* Add Pamm Withdrawal Drawer */}
      <PammDrawerForm
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Add Pamm Withdrawal"
        onSubmit={handleAddWithdrawalSubmit}
        loading={isSubmitting}
        submitText="Add"
      >
        {/* Investor Dropdown */}
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-foreground">
            Investor <span className="text-red-500">*</span>
          </label>
          <SourceCombobox
            items={
              investors.length > 0
                ? investors.map(inv => ({
                    value: inv.id,
                    label: `${inv.name} (${inv.accountNumber})`,
                  }))
                : [
                    { value: 'inv-1', label: 'Alex Johnson (ACC-99201)' },
                    { value: 'inv-2', label: 'Michael Brown (ACC-99202)' },
                    { value: 'inv-3', label: 'Sophia Taylor (ACC-99203)' },
                  ]
            }
            placeholder="Select Investor"
            value={selectedInvestorId}
            onChange={val => {
              setSelectedInvestorId(val);
              setFormErrors(prev => ({ ...prev, investorId: '' }));
            }}
          />
          {formErrors.investorId && (
            <p className="text-xs text-red-500 mt-1">{formErrors.investorId}</p>
          )}
        </div>

        {/* Withdrawal Type Dropdown */}
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-foreground">
            Withdrawal Type <span className="text-red-500">*</span>
          </label>
          <SourceCombobox
            items={[
              { value: 'Profit Withdrawal', label: 'Profit Withdrawal' },
              { value: 'Full Withdrawal', label: 'Full Withdrawal' },
              { value: 'Capital Reduction', label: 'Capital Reduction' },
            ]}
            placeholder="Select Withdrawal Type"
            value={withdrawalTypeInput}
            onChange={val => {
              setWithdrawalTypeInput(val);
              setFormErrors(prev => ({ ...prev, withdrawalType: '' }));
            }}
          />
          {formErrors.withdrawalType && (
            <p className="text-xs text-red-500 mt-1">
              {formErrors.withdrawalType}
            </p>
          )}
        </div>

        {/* Withdrawal Amount */}
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-foreground">
            Withdrawal Amount $ <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            placeholder="0.00"
            value={withdrawalAmountInput}
            onChange={e => {
              setWithdrawalAmountInput(e.target.value);
              setFormErrors(prev => ({ ...prev, withdrawalAmount: '' }));
            }}
            className="w-full bg-background border border-input rounded-lg px-3.5 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all"
          />
          {formErrors.withdrawalAmount && (
            <p className="text-xs text-red-500 mt-1">
              {formErrors.withdrawalAmount}
            </p>
          )}
        </div>

        {/* Comment */}
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-foreground">
            Comment <span className="text-red-500">*</span>
          </label>
          <textarea
            rows={3}
            placeholder="Enter comment or notes"
            value={commentInput}
            onChange={e => {
              setCommentInput(e.target.value);
              setFormErrors(prev => ({ ...prev, comment: '' }));
            }}
            className="w-full bg-background border border-input rounded-lg px-3.5 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all resize-none"
          />
          {formErrors.comment && (
            <p className="text-xs text-red-500 mt-1">{formErrors.comment}</p>
          )}
        </div>
      </PammDrawerForm>
    </div>
  );
}
